/**
 * Button click event target constants
 *
 * This file serves as documentation of all the button targets that can be used for filtering
 * in the Observability Tool. Each constant represents a unique button or UI element that
 * can be tracked via telemetry events.
 *
 * Observability Tool Filtering:
 * You can filter event logs in the Observability Tool using the metadata target values.
 * Example filter: Event Name = 'BUTTON_CLICK' AND Metadata Target = 'work-queue-priority'
 *
 * @example
 * import { useTrackEvent } from 'hooks/useTrackEvent';
 * import { ButtonClickTargets } from 'constants/buttonClickTargets';
 * const { trackButtonClick } = useTrackEvent({ tenant, product: 'IEM' });
 * trackButtonClick(ButtonClickTargets.LeftNavEntitySelection);
 */

export const ButtonClickTargets = {
	// Work Queue
	WorkQueuePriority: 'work-queue-priority',
	WorkQueueDate: 'work-queue-date',
	WorkQueueNew: 'work-queue-new',

	// Task Drawer
	TaskDrawerStatus: 'task-drawer-status',
	TaskDrawerChangeDueDate: 'task-drawer-change-due-date',
	TaskDrawerComments: 'task-drawer-comments',
	TaskDrawerViewRecordInCrm: 'task-drawer-view-record-in-crm',
	TaskDrawerClose: 'task-drawer-close',

	// Alerts View
	AlertsViewUnacknowledged: 'alerts-view-unacknowledged',
	AlertsViewAcknowledged: 'alerts-view-acknowledged',

	// Alerts Drawer
	AlertsDrawerClose: 'alerts-drawer-close',
	AlertsDrawerAcknowledge: 'alerts-drawer-acknowledge',
	AlertsDrawerUnacknowledge: 'alerts-drawer-unacknowledge',
	AlertsDrawerViewRecordInCrm: 'alerts-drawer-view-record-in-crm',
	AlertsDrawerViewAlertCenter: 'alerts-drawer-view-alert-center',
	AlertsDrawerDraftOutreachGenerate: 'alerts-drawer-draft-outreach-generate',
	AlertsDrawerDraftOutreachApply: 'alerts-drawer-draft-outreach-apply',
	AlertsDrawerCreateTask: 'alerts-drawer-create-task',

	// LP360
	LPDraftOutreachOpen: 'lp360-draft-outreach-open',
	LPDraftOutreachClose: 'lp360-draft-outreach-close',
	LPDraftOutreachGenerate: 'lp360-draft-outreach-generate',
	LPDraftOutreachApply: 'lp360-draft-outreach-apply',
	LP360BackToDashboard: 'lp360-back-to-dashboard',
	LP360InvestorExpand: 'lp360-investor-expand',
	LP360FRActiveFilter: 'lp360-fr-active-filter',
	LP360FRExistingInvestorFilter: 'lp360-fr-existing-investor-filter',
	LP360FRStageFilter: 'lp360-fr-stage-filter',
	LP360FRClosingRoundFilter: 'lp360-fr-closing-round-filter',
	LP360FRFromDateFilter: 'lp360-fr-from-date-filter',
	LP360FRToDateFilter: 'lp360-fr-to-date-filter',

	// Feedback Drawer
	FeedbackDrawerCancel: 'feedback-drawer-cancel',
	FeedbackDrawerSubmit: 'feedback-drawer-submit',

	// Active Raise Widget
	ActiveRaiseWidgetPin: 'active-raise-widget-pin',
	ActiveRaiseWidgetPinned: 'active-raise-widget-pinned',
	ActiveRaiseWidgetEntitySearch: 'active-raise-widget-entity-search',

	// Entity Dashboard
	EntityDashboardTypeFilter: 'entity-dashboard-type-filter',
	EntityDashboardStatusFilter: 'entity-dashboard-status-filter',
	EntityDashboardSearch: 'entity-dashboard-search',
	EntityDashboardRaiseDashboard: 'entity-dashboard-raise-dashboard',

	// Raise Dashboard
	RaiseDashboardTypeFilter: 'raise-dashboard-type-filter',
	RaiseDashboardActiveFilter: 'raise-dashboard-active-filter',
	RaiseDashboardExistingInvestorFilter:
		'raise-dashboard-existing-investor-filter',
	RaiseDashboardStageFilter: 'raise-dashboard-stage-filter',
	RaiseDashboardClosingRoundsFilter: 'raise-dashboard-closing-rounds-filter',
	RaiseDashboardFromDateFilter: 'raise-dashboard-from-date-filter',
	RaiseDashboardToDateFilter: 'raise-dashboard-to-date-filter',
	RaiseDashboardEdit: 'raise-dashboard-edit',
	RaiseDashboardConvertToInvestor: 'raise-dashboard-convert-to-investor',
	RaiseDashboardViewRecordInCrm: 'raise-dashboard-view-record-in-crm',

	// Fundraising Drawer
	FundraisingDrawerCancel: 'fundraising-drawer-cancel',
	FundraisingDrawerSave: 'fundraising-drawer-save',

	// Alert Center
	AlertCenterExplainSubscriptions: 'alert-center-explain-subscriptions',
	AlertCenterNewAlert: 'alert-center-new-alert',
	AlertCenterSubscribedFilter: 'alert-center-subscribed-filter',
	AlertCenterAllFilter: 'alert-center-all-filter',
	AlertCenterSubscribeToggle: 'alert-center-subscribe-toggle',
	AlertCenterUnsubscribeToggle: 'alert-center-unsubscribe-toggle',
	AlertCenterDelete: 'alert-center-delete',
	AlertCenterReportSearch: 'alert-center-report-search',
	AlertCenterReportSelect: 'alert-center-report-select',
	AlertCenterReportBackToAlerts: 'alert-center-reports-back-to-alerts',
	AlertCenterSuggestedSelect: 'alert-center-suggested-rule-select',
	AlertCenterSuggestedDeselect: 'alert-center-suggested-rule-deselect',
	AlertCenterSuggestedBackToReports: 'alert-center-suggested-back-to-reports',
	AlertCenterSuggestedRuleName: 'alert-center-suggested-rule-name',
	AlertCenterSuggestedRuleSeverity: 'alert-center-suggested-rule-severity',
	AlertCenterSuggestedRuleVisibility: 'alert-center-suggested-rule-visibility',
	AlertCenterSuggestedCancel: 'alert-center-suggested-alert-cancel',
	AlertCenterSuggestedCreateRule: 'alert-center-suggested-alert-create-rule',
	AlertCenterRunRuleAction: 'alert-center-run-rule-action',
	AlertCenterRunRuleCancel: 'alert-center-run-rule-cancel',
	AlertCenterRunRuleTest: 'alert-center-run-rule-test',
	AlertCenterRunRuleNow: 'alert-center-run-rule-now',
	AlertCenterRunRuleDone: 'alert-center-run-rule-done',

	// Left Navigation Panel
	LeftNavAllEntities: 'left-nav-all-entities',
	LeftNavEntitySelection: 'left-nav-entity-selection',
	LeftNavBackLaunchpad: 'left-nav-back-to-launchpad',

	// Settings Page
	SettingsGuardrailsSave: 'settings-guardrails-save',
};
